import { signal } from '@angular/core';
import { environment } from '@env';
import { Subject } from 'rxjs';

type ConsoleLevel = 'log' | 'info' | 'debug' | 'warn' | 'error';

interface IConsoleConfiguration
{
    production: boolean;
    type: string;
    developer_console_enabled: boolean;
}

export interface IConsoleEntry
{
    id: number;
    time: string;
    level: ConsoleLevel;
    message: string;
}

export class DeveloperConsoleStore
{
    public static readonly MAX_ENTRIES = 500;
    public static readonly MAX_MESSAGE_LENGTH = 16_000;
    private static readonly LEVELS: ConsoleLevel[] = [ 'log', 'info', 'debug', 'warn', 'error' ];

    public readonly enabled: boolean;
    private readonly recordedEntries = signal<IConsoleEntry[]>([]);
    public readonly entries = this.recordedEntries.asReadonly();
    private readonly entryChanges = new Subject<void>();
    public readonly changes = this.entryChanges.asObservable();
    private nextId = 0;
    private capturing = false;
    private readonly restoreMethods: Array<() => void> = [];

    private readonly onError = (event: ErrorEvent): void =>
    {
        this.record('error', [ event.error ?? `${ event.message } (${ event.filename }:${ event.lineno }:${ event.colno })` ]);
    };

    private readonly onRejection = (event: PromiseRejectionEvent): void =>
    {
        this.record('error', [ 'Unhandled promise rejection:', event.reason ]);
    };

    constructor(
        configuration: IConsoleConfiguration,
        private readonly consoleTarget: Console,
        private readonly eventTarget: Window,
    )
    {
        this.enabled = configuration.developer_console_enabled && !configuration.production
            && [ 'development', 'stage' ].includes(configuration.type);

        if (!this.enabled)
        {
            return;
        }

        for (const level of DeveloperConsoleStore.LEVELS)
        {
            const original = this.consoleTarget[level];
            const wrapped = (...args: unknown[]): void =>
            {
                this.record(level, args);
                original.apply(this.consoleTarget, args);
            };
            this.consoleTarget[level] = wrapped;
            this.restoreMethods.push(() =>
            {
                if (this.consoleTarget[level] === wrapped)
                {
                    this.consoleTarget[level] = original;
                }
            });
        }

        this.eventTarget.addEventListener('error', this.onError);
        this.eventTarget.addEventListener('unhandledrejection', this.onRejection);
    }

    public clear(): void
    {
        this.recordedEntries.set([]);
        this.entryChanges.next();
    }

    public destroy(): void
    {
        for (const restore of this.restoreMethods)
        {
            restore();
        }
        this.eventTarget.removeEventListener('error', this.onError);
        this.eventTarget.removeEventListener('unhandledrejection', this.onRejection);
    }

    private record(level: ConsoleLevel, args: unknown[]): void
    {
        if (!this.enabled || this.capturing)
        {
            return;
        }

        this.capturing = true;
        try
        {
            const values = typeof args[0] === 'string' && args[0].startsWith('%c[')
                ? [ args[0].slice(2), ...args.slice(2) ]
                : args;
            const message = values.map((value) => this.format(value)).join(' ')
                .slice(0, DeveloperConsoleStore.MAX_MESSAGE_LENGTH);
            const entry: IConsoleEntry = {
                id: ++this.nextId,
                time: new Date().toISOString().slice(11, 23),
                level,
                message,
            };
            this.recordedEntries.update((entries) => [
                ...entries.slice(-(DeveloperConsoleStore.MAX_ENTRIES - 1)), entry,
            ]);
            this.entryChanges.next();
        }
        finally
        {
            this.capturing = false;
        }
    }

    private format(value: unknown): string
    {
        try
        {
            if (typeof value === 'string')
            {
                return value.slice(0, DeveloperConsoleStore.MAX_MESSAGE_LENGTH);
            }
            if (value instanceof Error)
            {
                return value.stack ?? `${ value.name }: ${ value.message }`;
            }
            const seen = new WeakSet<object>();
            let visited = 0;
            return JSON.stringify(value, (_key, item: unknown) =>
            {
                if (++visited > 1_000)
                {
                    return '[Truncated]';
                }
                if (typeof item === 'bigint')
                {
                    return `${ item }n`;
                }
                if (item instanceof Error)
                {
                    return item.stack ?? `${ item.name }: ${ item.message }`;
                }
                if (item && typeof item === 'object')
                {
                    if (seen.has(item))
                    {
                        return '[Circular]';
                    }
                    seen.add(item);
                }
                return item;
            }) ?? String(value);
        }
        catch
        {
            return '[Unserializable value]';
        }
    }
}

export const developerConsole = new DeveloperConsoleStore(environment, globalThis.console, window);
